export const SODIOHOME_DEVICE_TYPES = [
  { id: 3, name: 'Growatt' },
  { id: 4, name: 'inesco 12K - WR Hybrid' }
] as const;

export const getDeviceTypeName = (deviceId: number): string =>
  SODIOHOME_DEVICE_TYPES.find(d => d.id === deviceId)?.name ?? '';

// [inverter][cluster] = batteryCount
export type PresetConfig = number[][];

// 3D array: [inverter][cluster][batteryIndex] = serialNumber
export type BatterySnTree = string[][][];

// Device-aware presets: keyed by device ID, then model name
// Device 3 = Growatt, Device 4 = inesco 12K
export const INSTALLATION_PRESETS: Record<number, Record<string, PresetConfig>> = {
  3: {
    'sodistore home 9':  [[2]],
    'sodistore home 18': [[4]],
  },
  4: {
    'sodistore home 9':    [[1, 1]],
    'sodistore home 13.5': [[2, 1]],
    'sodistore home 18':   [[2, 2]],
    'sodistore home 27':   [[2, 2], [1, 1]],
    'sodistore home 36':   [[2, 2], [2, 2]],
    // R3 revision: same battery counts (and therefore the same kWh) as the models
    // above, but the batteries hang off ONE cluster per inverter instead of two.
    // Kept as separate model names rather than replacing the originals — existing
    // installations keep their two-cluster topology and their serial-number tree.
    'sodistore home 9 R3':    [[2]],
    'sodistore home 13.5 R3': [[3]],
    'sodistore home 18 R3':   [[4]],
    'sodistore home 27 R3':   [[3], [3]],
    'sodistore home 36 R3':   [[4], [4]],
  },
};

// Per-cluster current limit in A — the same figure for max charge and max discharge.
//
// Pre-R3 models: 95 A per battery, so the limit scales with how many batteries hang
// off the cluster (1 → 95 A, 2 → 190 A).
//
// R3: a flat 115 A per cluster NO MATTER how many batteries are inside it. R3 wires
// every battery of an inverter onto ONE input, and that input is rated 115 A — so
// applying the per-battery rule would seed 380 A on a 4-battery R3 cluster, more than
// three times the real limit, on hardware that would actually try to honour it.
export const CURRENT_PER_BATTERY_A = 95;
export const R3_CLUSTER_CURRENT_A = 115;

// R3 is identified by the model-name suffix, which is also what the ERP stores. Anchored
// so it matches the suffix and not an "R3" appearing anywhere else in a name.
export const isR3Model = (model: string): boolean => /\sR3$/.test((model || '').trim());

export const clusterCurrentLimitA = (model: string, batteryCount: number): number =>
  isR3Model(model) ? R3_CLUSTER_CURRENT_A : batteryCount * CURRENT_PER_BATTERY_A;

// Minimum-SoC prefill, in %. R3 ships with a 5 % floor; older models have no
// monitor-side default at all, so they keep showing exactly what the device reports.
//
// PREFILL ONLY — the device's own reported MinSoc always wins, and this is written back
// only if the user saves the form, exactly like the per-cluster current limit above.
// Returning undefined (rather than a number) for non-R3 is what keeps that promise: the
// caller can `?? defaultMinSoC(model)` without inventing a value for the older models.
export const R3_DEFAULT_MIN_SOC = 5;

export const defaultMinSoC = (model: string): number | undefined =>
  isR3Model(model) ? R3_DEFAULT_MIN_SOC : undefined;

export const getPresetsForDevice = (deviceId: number): Record<string, PresetConfig> =>
  INSTALLATION_PRESETS[deviceId] ?? {};

export const buildSodistoreProPreset = (inverterCount: number): PresetConfig =>
  Array.from({ length: inverterCount }, () => [2, 2]);

// Sodistore Pro: one inverter carries 2 clusters of 2 batteries. A battery is 4.5 kWh
// (consistent across every Home preset above), so an inverter is 18 kWh.
//
// ⚠ SINGLE SOURCE OF TRUTH for the Pro kWh↔inverter relationship. Every Pro model
// name, preset and display label below is derived from this table, so if the real
// mapping turns out to be shifted by one inverter, this object is the only edit.
export const SODISTORE_PRO_INVERTERS_BY_KWH: Record<number, number> = {
  54: 3,
  72: 4,
  90: 5,
  108: 6
};

export const sodistoreProModelName = (kwh: number): string => `sodistore pro ${kwh}`;

export const SODISTORE_PRO_PRESETS: Record<string, PresetConfig> = Object.fromEntries(
  Object.entries(SODISTORE_PRO_INVERTERS_BY_KWH).map(([kwh, inverters]) => [
    sodistoreProModelName(Number(kwh)),
    buildSodistoreProPreset(inverters)
  ])
);

// Home presets are keyed by device; Pro shares device 4 with Home, so the model
// list has to be chosen by product first.
export const getPresets = (
  product: number,
  deviceId: number
): Record<string, PresetConfig> =>
  product === 5 ? SODISTORE_PRO_PRESETS : getPresetsForDevice(deviceId);

// Single lookup used by every tab. Falls back to the legacy Sodistore Pro format,
// where installationModel held a bare inverter count ("2", "3") instead of a kWh
// label — those rows are read as before and are never rewritten automatically.
//
// Resolution is by NAME first and only then by product, deliberately: Pro and Home
// model names cannot collide, so a topology never depends on the caller passing the
// right product. Device still gates the Home map, because the same Home name means
// different hardware per device ('sodistore home 18' is [[4]] on 3, [[2,2]] on 4).
export const resolvePresetConfig = (
  product: number,
  deviceId: number,
  model: string
): PresetConfig | null => {
  if (!model) return null;

  const named = SODISTORE_PRO_PRESETS[model] ?? getPresetsForDevice(deviceId)[model];
  if (named) return named;

  if (product === 5) {
    const legacyInverterCount = parseInt(model, 10);
    if (!isNaN(legacyInverterCount) && legacyInverterCount > 0) {
      return buildSodistoreProPreset(legacyInverterCount);
    }
  }
  return null;
};

// Display only — never written back to the DB. Legacy Sodistore Pro rows store a bare
// inverter count ("4", "6"); show them as the kWh model they correspond to so lists read
// consistently with the dropdowns. Anything unrecognised is shown verbatim rather than hidden.
export const formatInstallationModel = (product: number, model: string): string => {
  if (!model) return '';
  if (product !== 5 || SODISTORE_PRO_PRESETS[model]) return model;

  const legacyInverterCount = parseInt(model, 10);
  const kwh = Object.entries(SODISTORE_PRO_INVERTERS_BY_KWH).find(
    ([, inverters]) => inverters === legacyInverterCount
  )?.[0];

  return kwh ? sodistoreProModelName(Number(kwh)) : model;
};

export const buildEmptyTree = (preset: PresetConfig): BatterySnTree => {
  return preset.map((inv) =>
    inv.map((batteryCount) => Array.from({ length: batteryCount }, () => ''))
  );
};

export const parseBatterySnTree = (
  raw: string,
  preset: PresetConfig
): BatterySnTree => {
  if (!raw || raw.trim() === '') {
    return buildEmptyTree(preset);
  }

  const isStructured = raw.includes('/') || raw.includes('|');

  if (isStructured) {
    const inverters = raw.split('/');
    return preset.map((invPreset, invIdx) => {
      const clusterStr = inverters[invIdx] || '';
      const clusters = clusterStr ? clusterStr.split('|') : [];
      return invPreset.map((batteryCount, clIdx) => {
        const batteries = clusters[clIdx]
          ? clusters[clIdx].split(',').map((s) => s.trim())
          : [];
        return Array.from({ length: batteryCount }, (_, i) => batteries[i] || '');
      });
    });
  }

  // Legacy flat format: distribute by preset layout
  const allSns = raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s !== '');
  let idx = 0;
  return preset.map((inv) =>
    inv.map((batteryCount) =>
      Array.from({ length: batteryCount }, () => allSns[idx++] || '')
    )
  );
};

export const serializeBatterySnTree = (tree: BatterySnTree): string => {
  return tree
    .map((inv) => inv.map((cluster) => cluster.join(',')).join('|'))
    .join('/');
};

export const remapTree = (
  oldTree: BatterySnTree,
  newPreset: PresetConfig
): BatterySnTree => {
  return newPreset.map((inv, invIdx) =>
    inv.map((batteryCount, clIdx) =>
      Array.from(
        { length: batteryCount },
        (_, batIdx) => oldTree[invIdx]?.[clIdx]?.[batIdx] || ''
      )
    )
  );
};

export const computeFlatValues = (
  preset: PresetConfig,
  tree: BatterySnTree
) => {
  const totalBatteries = preset.flat().reduce((a, b) => a + b, 0);
  const totalClusters = preset.reduce((sum, inv) => sum + inv.length, 0);
  return {
    batteryNumber: totalBatteries,
    batteryClusterNumber: totalClusters,
    batterySerialNumbers: serializeBatterySnTree(tree),
  };
};

export interface ActiveCluster {
  invIdx: number;
  clIdx: number;
  flatIdx: number;
}

export const getActiveClusters = (raw: string): ActiveCluster[] => {
  if (!raw || raw.trim() === '') return [];
  if (!raw.includes('/') && !raw.includes('|')) return [];

  const result: ActiveCluster[] = [];
  let flatIdx = 0;
  raw.split('/').forEach((invStr, invIdx) => {
    invStr.split('|').forEach((clStr, clIdx) => {
      const hasSn = clStr
        .split(',')
        .some((s) => s.trim() !== '');
      if (hasSn) {
        result.push({ invIdx, clIdx, flatIdx });
      }
      flatIdx += 1;
    });
  });
  return result;
};

// Every cluster slot a topology has, in the same flat order getActiveClusters uses.
//
// This is the no-serial-numbers-yet counterpart of getActiveClusters: it says which
// clusters EXIST, not which ones are filled in. Callers that used to fall back to
// "2 clusters per inverter" must use this instead — that guess is wrong for any
// model with one cluster per inverter (every "R3" preset), where it collapses two
// inverters into one and reads the second inverter's data off the first.
export const clustersFromPreset = (preset: PresetConfig): ActiveCluster[] => {
  const result: ActiveCluster[] = [];
  let flatIdx = 0;
  preset.forEach((inv, invIdx) => {
    inv.forEach((_, clIdx) => {
      result.push({ invIdx, clIdx, flatIdx });
      flatIdx += 1;
    });
  });
  return result;
};

export const wouldLoseData = (
  oldTree: BatterySnTree,
  newPreset: PresetConfig
): boolean => {
  for (let invIdx = 0; invIdx < oldTree.length; invIdx++) {
    for (let clIdx = 0; clIdx < (oldTree[invIdx] || []).length; clIdx++) {
      for (
        let batIdx = 0;
        batIdx < (oldTree[invIdx][clIdx] || []).length;
        batIdx++
      ) {
        const sn = oldTree[invIdx][clIdx][batIdx];
        if (sn && sn.trim() !== '') {
          if (invIdx >= newPreset.length) return true;
          if (clIdx >= (newPreset[invIdx] || []).length) return true;
          const newBatCount = newPreset[invIdx]?.[clIdx] ?? 0;
          if (batIdx >= newBatCount) return true;
        }
      }
    }
  }
  return false;
};
